// Code generated by @hex-insights/frontend-generator; While this comment is present this file may be overwritten. If manual edits are made, remove this comment.
import React from "react";
import { CreateFunction, UpdateFunction } from "@hex-insights/app-modules";
import { ClassNameProps, DistributiveOmit, StyleProps } from "@hex-insights/core";
import { Form, FormEditButtonPair, FormState, SubmitButton } from "@hex-insights/forms";
import {
	StudentOTCMedicationCreateMutation,
	StudentOTCMedicationDetailQuery,
	StudentOTCMedicationUpdateMutation,
} from "../../../Utilities";
import { StudentOTCMedicationFormState, StudentOTCMedicationFormValues } from "../../FormUtilities";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import {
	useCreateFormControl,
	useCreateFormSubmission,
	useDetailFormControl,
	useDetailFormSubmission,
	useReadOnlyForm,
} from "./hooks";

export {
	type UseCreateFormArgs,
	useCreateForm,
	type UseDetailFormArgs,
	useDetailForm,
	type UseReadOnlyFormArgs,
	useReadOnlyForm,
} from "./hooks";

export type ControlledCreateProps = Omit<
	CreateProps<StudentOTCMedicationCreateMutation["createStudentOTCMedication"]>,
	"formState" | "create"
> & {
	formStateArgs?: StudentOTCMedicationFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the StudentOTCMedication model using an internally managed form state.
 */
export function ControlledCreate({ formStateArgs, ...props }: ControlledCreateProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <Create formState={formState} create={create} {...props} />;
}

export type CreateProps<T> = {
	formState: FormState<StudentOTCMedicationFormValues.Create>;
	create: CreateFunction<StudentOTCMedicationFormValues.Create, T>;
	onSuccess: (formState: FormState<StudentOTCMedicationFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<StudentOTCMedicationFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the StudentOTCMedication model using the given form state.
 */
export function Create<T>({ formState, create, onSuccess, formNameSuffix, ...props }: CreateProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "studentOTCMedication.create" + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<CreateFields formState={formState} />

				<Form.ActionBar>
					<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
						Submit
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}

type CreateFieldsProps = {
	formState: FormState<StudentOTCMedicationFormValues.Create>;
};

function CreateFields({ formState }: CreateFieldsProps) {
	return (
		<React.Fragment>
			<Field.IsAllowed formState={formState} />
			<Field.Notes formState={formState} />
			<Field.Medication.Select formState={formState} />
			<Field.Student.Select formState={formState} />
		</React.Fragment>
	);
}

export type ControlledDetailProps = Omit<
	DetailProps<StudentOTCMedicationUpdateMutation["updateStudentOTCMedication"]>,
	"formState" | "update"
> & {
	formStateArgs?: Partial<DistributiveOmit<StudentOTCMedicationFormState.UseDetailFormStateArgs, "initialFormValues">>;
};

/**
 * Detail form of the StudentOTCMedication model using an internally managed form state.
 */
export function ControlledDetail({ formStateArgs, ...props }: ControlledDetailProps) {
	const { formState, update } = useDetailFormControl({
		...formStateArgs,
		studentOTCMedication: props.studentOTCMedication,
	});

	return <Detail formState={formState} update={update} {...props} />;
}

export type DetailProps<T> = {
	studentOTCMedication: StudentOTCMedicationDetailQuery["studentOTCMedication"];
	formState: FormState<StudentOTCMedicationFormValues.Detail>;
	update: UpdateFunction<StudentOTCMedicationFormValues.Detail, T>;
	onSuccess: (formState: FormState<StudentOTCMedicationFormValues.Detail>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<StudentOTCMedicationFormValues.Detail> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Detail form of the StudentOTCMedication model using the given form state.
 */
export function Detail<T>({
	formState,
	studentOTCMedication,
	update,
	onSuccess,
	formNameSuffix,
	...props
}: DetailProps<T>) {
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	const formName =
		`studentOTCMedication.detail.${studentOTCMedication.id}` + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<FormEditButtonPair formState={formState} />

				<DetailFields formState={formState} studentOTCMedication={studentOTCMedication} />
			</Form.Column>
		</Form>
	);
}

export type ControlledReadOnlyProps = Omit<ReadOnlyProps, "formState"> & {
	formStateArgs?: Partial<
		DistributiveOmit<StudentOTCMedicationFormState.UseReadOnlyFormStateArgs, "initialFormValues">
	>;
};

/**
 * Read-only detail form of the StudentOTCMedication model using an internally managed form state.
 */
export function ControlledReadOnly({ formStateArgs, ...props }: ControlledReadOnlyProps) {
	const { formState } = useReadOnlyForm({ ...formStateArgs, studentOTCMedication: props.studentOTCMedication });

	return <ReadOnly formState={formState} {...props} />;
}

export type ReadOnlyProps = {
	studentOTCMedication: StudentOTCMedicationDetailQuery["studentOTCMedication"];
	formState: FormState<StudentOTCMedicationFormValues.Detail>;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Read-only detail form of the StudentOTCMedication model using the given form state.
 */
export function ReadOnly({ formState, studentOTCMedication, className, style }: ReadOnlyProps) {
	return (
		<div className={className} style={style}>
			<Form.Column>
				<DetailFields formState={formState} studentOTCMedication={studentOTCMedication} />
			</Form.Column>
		</div>
	);
}

type DetailFieldsProps = {
	studentOTCMedication: StudentOTCMedicationDetailQuery["studentOTCMedication"];
	formState: FormState<StudentOTCMedicationFormValues.Detail>;
};

function DetailFields({ formState, studentOTCMedication }: DetailFieldsProps) {
	return (
		<React.Fragment>
			<Field.IsAllowed formState={formState} />
			<Field.Notes formState={formState} />
			<Field.Medication.Select formState={formState} currentInstance={studentOTCMedication.medication} />
			<Field.Student.Select formState={formState} currentInstance={studentOTCMedication.student} />
		</React.Fragment>
	);
}

export type CreateFieldsInStudentProps = {
	formState: FormState<StudentOTCMedicationFormValues.CreateInStudent>;
};

export function CreateFieldsInStudent({ formState }: CreateFieldsInStudentProps) {
	return (
		<React.Fragment>
			<Field.IsAllowed formState={formState} />
			<Field.Notes formState={formState} />
			<Field.Medication.Select formState={formState} />
		</React.Fragment>
	);
}
